import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userManager } from '../../config/oidc';
// Import types from the new types file instead of creating them here
import { CategoryDto, TaxonomyDto, PropertyType, CategoryProperty } from '../../types/category';
import { safeLocalStorageGet } from '../middleware/localStorageMiddleware';

interface CategoryDisplayPreferences {
  [categoryId: string]: {
    showAggregateMap?: boolean;
  };
}

interface CategoryState {
  taxonomy: TaxonomyDto[] | null;
  loading: boolean;
  error: string | null;
  sessionExpired: boolean;
  displayPreferences: CategoryDisplayPreferences;
}

const initialState: CategoryState = {
  taxonomy: null,
  loading: false,
  error: null,
  sessionExpired: false,
  displayPreferences: {},
};

// Keep your async thunks and slice logic here but remove the type definitions
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (orgId: string | undefined, { rejectWithValue, getState }) => {
    try {
      // Use provided orgId, or get from Redux store, or fall back to localStorage
      let dynamicSegment = orgId;
      if (!dynamicSegment) {
        const state = getState() as { organizations?: { dynamicSegment?: string } };
        dynamicSegment = state.organizations?.dynamicSegment || safeLocalStorageGet('orgadmin_dynamic_segment') || '';
      }

      const apiUrl = import.meta.env.VITE_API_ORGADMIN_API_BASE || 'https://test-api.sirktek.com/orgadmin/v1';
      const urlWithParams = `${apiUrl}/${dynamicSegment}/category/`;

      // Get access token from OIDC user manager
      const user = await userManager.getUser();
      const token = user?.access_token;
      if (!token) {
        return rejectWithValue({ message: 'Not authenticated', sessionExpired: true });
      }

      const response = await fetch(urlWithParams, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json = await response.json();
      // API may return a single root TaxonomyDto with children, or an array directly
      if (Array.isArray(json)) {
        return json as TaxonomyDto[];
      } else if (json && Array.isArray(json.children)) {
        // json.category is the org-level root wrapper — not a navigable taxonomy node,
        // so only its children are returned as the taxonomy tree.
        return json.children as TaxonomyDto[];
      }
      console.warn('[fetchCategories] Unexpected API response shape — expected array or { children: [] }', json);
      return rejectWithValue({ message: 'Unexpected API response shape', sessionExpired: false });
    } catch (err) {
      if (
        err instanceof Error &&
        (
          err.message.includes('login_required') ||
          err.message.includes('invalid_grant') ||
          err.message.includes('consent_required') ||
          err.message.includes('unauthorized') ||
          err.message.includes('401') ||
          err.message.includes('403')
        )
      ) {
        return rejectWithValue({
          message: err.message,
          sessionExpired: true
        });
      }

      return rejectWithValue({
        message: err instanceof Error ? err.message : 'Unknown error',
        sessionExpired: false
      });
    }
  },
  {
    // Skip fetching if we already have taxonomy data and not loading
    condition: (_orgId, { getState }) => {
      const state = getState() as { categories: { taxonomy: TaxonomyDto[] | null; loading: boolean } };
      const { taxonomy, loading } = state.categories;

      // Skip if already loading
      if (loading) {
        return false;
      }

      // Skip if we already have taxonomy data (null = not yet fetched / error, [] = legitimately empty)
      if (taxonomy !== null) {
        return false;
      }

      return true;
    }
  }
);

const categorySlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearCategoriesError: (state) => {
      state.error = null;
    },
    resetSessionExpired: (state) => {
      state.sessionExpired = false;
    },
    clearTaxonomy: (state) => {
      state.taxonomy = null;
      state.error = null;
    },
    toggleAggregateMap: (state, action: PayloadAction<string>) => {
      const categoryId = action.payload;
      if (!state.displayPreferences[categoryId]) {
        state.displayPreferences[categoryId] = {};
      }
      const current = state.displayPreferences[categoryId].showAggregateMap ?? false;
      state.displayPreferences[categoryId].showAggregateMap = !current;
    },
    setAggregateMapPreference: (state, action: PayloadAction<{ categoryId: string; show: boolean }>) => {
      const { categoryId, show } = action.payload;
      if (!state.displayPreferences[categoryId]) {
        state.displayPreferences[categoryId] = {};
      }
      state.displayPreferences[categoryId].showAggregateMap = show;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.sessionExpired = false;
      })
      .addCase(fetchCategories.fulfilled, (state, action: PayloadAction<TaxonomyDto[]>) => {
        state.loading = false;
        state.taxonomy = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const payload = action.payload as { message: string, sessionExpired: boolean };
          state.error = payload.message;
          state.sessionExpired = payload.sessionExpired;
        } else {
          state.error = action.error.message || 'Unknown error';
        }
      });
  },
});

export const { clearCategoriesError, resetSessionExpired, clearTaxonomy, toggleAggregateMap, setAggregateMapPreference } = categorySlice.actions;
export default categorySlice.reducer;

// Re-export the types for convenience
export type { PropertyType, CategoryDto, TaxonomyDto, CategoryProperty };